import { computed, toValue } from 'vue';
import type {
  Position,
  PositionDuplicateData,
  PositionMoveFileSingleData,
  PositionMoveFilesBulkData,
  PositionUpdateData,
} from '@cutr/constants/position';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  bulkCreatePositions,
  createPosition,
  deletePosition,
  duplicatePosition,
  getPositionsForVariant,
  getSummaryByPosition,
  moveFilesBulkToPosition,
  moveFileSingleToPosition,
  sortPositions,
  updatePosition,
  getArchivedPositionsForVariant,
  restorePosition,
} from '@/api/positions';
import { positionKeys, scopeAnnotationKeys } from './keys';
import { useInvalidateParts } from './parts';
import { appendRow, patchRow, removeRow } from './shared/listUpdaters';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags } from './shared/types';
import { invalidateOrderFiles } from './orderFiles';

export function positionSummaryOptions(variantId: string) {
  return queryOptions({
    queryKey: positionKeys.summary(variantId),
    queryFn: ({ signal }) => getSummaryByPosition(variantId, signal),
    // Part material writes invalidate this now, but hardware writes still
    // don't. CTR-15601 is what removes the override.
    staleTime: 0,
  });
}

export function positionsListOptions(variantId: string) {
  return queryOptions({
    queryKey: positionKeys.list(variantId),
    queryFn: ({ signal }) => getPositionsForVariant(variantId, signal),
  });
}

export function archivedPositionsListOptions(variantId: string) {
  return queryOptions({
    queryKey: positionKeys.archived(variantId),
    queryFn: ({ signal }) => getArchivedPositionsForVariant(variantId, signal),
  });
}

/**
 * Returns a new array of new position objects ordered to match `sorted`, with
 * `sortOrder` reassigned 1..n.
 * Positions absent from `sorted` are appended in their existing relative order.
 */
export function reorderPositions(
  current: Position[],
  sorted: Position[]
): Position[] {
  const desiredOrder = new Map(sorted.map((item, index) => [item.id, index]));

  return [...current]
    .sort(
      (a, b) =>
        (desiredOrder.get(a.id) ?? Infinity) -
        (desiredOrder.get(b.id) ?? Infinity)
    )
    .map((position, index) => ({ ...position, sortOrder: index + 1 }));
}

export function usePositionSummary(variantId: MaybeId) {
  const query = useScopedQuery(variantId, positionSummaryOptions);

  return { summaries: computed(() => query.data.value ?? []), query };
}

export function usePositionsQuery(variantId: MaybeId, flags?: QueryFlags) {
  const query = useScopedQuery(variantId, positionsListOptions, flags);

  return { positions: computed(() => query.data.value ?? []), query };
}

export function useEnsurePositionData() {
  const queryClient = useQueryClient();

  return (variantId: string) => {
    return queryClient.ensureQueryData(positionsListOptions(variantId));
  };
}

export function useArchivedPositionsQuery(
  variantId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(variantId, archivedPositionsListOptions, flags);

  return {
    query,
    positions: computed(() => query.data.value ?? []),
  };
}

export type ReorderVariables = {
  orderVariantId: string;
  positions: Position[];
};

export type MoveFileSingleVariables = {
  orderVariantId: string;
  positionId: string;
  payload: PositionMoveFileSingleData;
};

export type MoveFilesBulkVariables = PositionMoveFilesBulkData & {
  orderVariantId: string;
};

export function usePositionMutations(orderId: MaybeId) {
  const queryClient = useQueryClient();

  const setList = (
    variantId: string,
    updater: (list: Position[] | undefined) => Position[] | undefined
  ) =>
    queryClient.setQueryData<Position[]>(positionKeys.list(variantId), updater);

  const appendPosition = (position: Position) =>
    setList(position.orderVariantId, appendRow(position));

  const patchPosition = (
    position: Partial<Position> & Pick<Position, 'id' | 'orderVariantId'>
  ) => setList(position.orderVariantId, patchRow<Position>(position));

  const invalidatePositions = (variantId: string) =>
    queryClient.invalidateQueries({ queryKey: positionKeys.all(variantId) });

  const invalidateSummary = (variantId: string) =>
    queryClient.invalidateQueries({
      queryKey: positionKeys.summary(variantId),
    });

  const invalidateFiles = (variantId: string) =>
    invalidateOrderFiles(queryClient, {
      orderId: toValue(orderId),
      variantId,
    });

  const invalidateParts = useInvalidateParts();

  // No snapshot to roll back to: delete and reorder can overlap, and restoring
  // one's snapshot would undo the other's write. onSettled reconciles instead.
  const applyOptimistic = async (
    variantId: string,
    updater: (list: Position[] | undefined) => Position[] | undefined
  ) => {
    await queryClient.cancelQueries({ queryKey: positionKeys.list(variantId) });
    setList(variantId, updater);
  };

  const create = useMutation({
    mutationFn: createPosition,
    onSuccess: appendPosition,
  });

  const duplicate = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: PositionDuplicateData;
    }) => duplicatePosition(id, payload),
    onSuccess(position) {
      appendPosition(position);
      // The copy comes with its own files, so the summary moves with them.
      invalidateSummary(position.orderVariantId);
      invalidateFiles(position.orderVariantId);
    },
  });

  const update = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: PositionUpdateData;
    }) => updatePosition(id, payload),
    onSuccess(position, { payload }) {
      patchPosition(position);
      // (de)scoping a position changes which parts are eligible for nesting and
      // material assignment.
      if ('isInScope' in payload) invalidateParts(position.orderVariantId);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id }: Pick<Position, 'id' | 'orderVariantId'>) =>
      deletePosition(id),
    onMutate: ({ id, orderVariantId }) =>
      applyOptimistic(orderVariantId, removeRow(id)),
    onSettled(_data, _error, { orderVariantId }) {
      invalidatePositions(orderVariantId);
      // The deleted position's files return to the inbox.
      invalidateFiles(orderVariantId);
    },
  });
  const restore = useMutation({
    mutationFn: ({ id }: Pick<Position, 'id' | 'orderVariantId'>) =>
      restorePosition(id),
    onSettled(_data, _error, { orderVariantId }) {
      invalidatePositions(orderVariantId);
      invalidateFiles(orderVariantId);
    },
  });

  const reorder = useMutation({
    mutationFn: ({ positions }: ReorderVariables) =>
      sortPositions(positions.map((position) => position.id)),
    onMutate: ({ orderVariantId, positions }) =>
      applyOptimistic(
        orderVariantId,
        (current) => current && reorderPositions(current, positions)
      ),
    // Writes sortOrder and nothing else, so no cadFile changed.
    onSettled: (_data, _error, { orderVariantId }) =>
      invalidatePositions(orderVariantId),
  });

  const invalidateOnMove = (orderVariantId: string) => {
    invalidatePositions(orderVariantId);
    invalidateFiles(orderVariantId);
    // A quoting file moved away from its design file loses its scope-annotation links, on a design file the client can't name.
    queryClient.invalidateQueries({ queryKey: scopeAnnotationKeys.all() });
  };

  // The server also moves the quoting children of a design file and, when it
  // replaces one, revises it — ids the client never sees, so the files list
  // cannot be patched from the ids we sent.
  const moveFileSingle = useMutation({
    mutationFn: ({ positionId, payload }: MoveFileSingleVariables) =>
      moveFileSingleToPosition(positionId, payload),
    onSettled(_data, _error, { orderVariantId }) {
      invalidateOnMove(orderVariantId);
    },
  });

  const moveFilesBulk = useMutation({
    mutationFn: ({ orderVariantId, ...payload }: MoveFilesBulkVariables) =>
      moveFilesBulkToPosition(payload),
    onSettled(_data, _error, { orderVariantId }) {
      invalidateOnMove(orderVariantId);
    },
  });

  const bulkCreate = useMutation({
    mutationFn: bulkCreatePositions,
    onSettled(_data, _error, { orderVariantId }) {
      invalidatePositions(orderVariantId);
      invalidateFiles(orderVariantId);
    },
  });

  return {
    createPosition: create.mutateAsync,
    duplicatePosition: (id: string, payload: PositionDuplicateData = {}) =>
      duplicate.mutateAsync({ id, payload }),
    updatePosition: (id: string, payload: PositionUpdateData) =>
      update.mutateAsync({ id, payload }),
    deletePosition: (position: Pick<Position, 'id' | 'orderVariantId'>) =>
      remove.mutateAsync(position),
    restorePosition: (position: Pick<Position, 'id' | 'orderVariantId'>) =>
      restore.mutateAsync(position),
    setSortingOrder: (variables: ReorderVariables) =>
      reorder.mutateAsync(variables),
    moveFileSingle: (variables: MoveFileSingleVariables) =>
      moveFileSingle.mutateAsync(variables),
    moveFilesBulk: (variables: MoveFilesBulkVariables) =>
      moveFilesBulk.mutateAsync(variables),
    bulkCreatePositions: bulkCreate.mutateAsync,
    patchPosition,
    invalidateOnMove,
  };
}
